import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatPaginator } from '@angular/material';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Router } from '@angular/router';
import { ToastsManager } from 'ng2-toastr';
import { tap } from 'rxjs/operators';
import { Subscription } from 'rxjs/Subscription';
import { LoggerService } from 'app/services/logger.service';
import { OfferIface } from '../../../interfaces/offer.iface';
import { RESTDataSource } from '../../../helpers/rest.datasource';
import { OffersService } from '../../../services/offers.service';
import { Tag } from '../../../interfaces/tag';
import { OffersMassEditionService } from '../../../services/offers-mass-edition.service';
import * as FileSaver from 'file-saver';
import { WithdrawOffersDialogComponent } from './witdraw-offers-dialog/withdraw-offers-dialog.component';
import { MassStatusChangeDialogComponent } from 'app/modules/offers/offer-list/mass-status-change-dialog/mass-status-change-dialog.component';
import { Status } from 'app/interfaces/status';
import { StatusService } from 'app/services/status.service';
import { Count } from 'app/interfaces/crud.interface';
import { MassTagsChangeDialogComponent } from 'app/modules/offers/offer-list/mass-tags-change-dialog/mass-tags-change-dialog.component';
import { OfferInitialCsvDialogComponent } from './offer-initial-csv/offer-initial-csv-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import * as iconv from 'iconv-lite';
import { AuthService } from '../../../services/auth.service';
import { TagsFilterComponent } from '../../../shared/components/tags-filter/tags-filter.component';
import { TagsServiceTypeEnum } from '../../../enums/tags-service-type.enum';

export interface OfferCount {
  count: Count;
  status: Status;
  isToggled;
}

@Component({
  selector: 'app-offer-list',
  templateUrl: './offer-list.component.html',
  styleUrls: ['./offer-list.component.scss']
})
export class OfferListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild('myPond')
  myPond: any;
  @ViewChild(TagsFilterComponent)
  TagsFilter: TagsFilterComponent;
  public readonly tagsServiceTypeEnum = TagsServiceTypeEnum;
  public dataSource: RESTDataSource<OfferIface>;
  public displayedColumns = [
    'name',
    'availableSince',
    'availableUntil',
    'availableInLocations',
    'availableInChannels',
    'availableForCustomerSegments',
    'code',
    'linkToTermsOfService',
    'price',
    'period',
    'activationPrice',
    'updatedAt',
    'editOwner',
    'initialCsv'
  ];
  public isLoadingResults = false;
  public subscription: Subscription;
  public activeTags: Tag[] = [];
  public statuses: Status[];
  public offersFilteredByStatusCode = <OfferCount[]>[];
  public selectedStatus: Status;
  public serviceName = OffersService.name;
  public stepperSelectedIndex: Number;

  constructor(
    private offersService: OffersService,
    private offersMassEditionService: OffersMassEditionService,
    private dialog: MatDialog,
    private toastr: ToastsManager,
    private statusService: StatusService,
    public authService: AuthService,
    public translate: TranslateService,
    public router: Router
  ) {}

  static downloadFile(data: string): void {
    const blob = new Blob([iconv.encode(data, 'win-1250')], { type: 'text/csv' });
    FileSaver.saveAs(blob, 'offers.csv');
  }

  ngOnInit() {
    LoggerService.log('OfferListComponent::onInit');
    this.dataSource = new RESTDataSource(this.offersService);
    this.subscription = this.dataSource.loading$.subscribe(state => {
      this.isLoadingResults = state;
    });
    this.paginator.pageSize = 10;
    this.setStatusesCount();
  }

  ngAfterViewInit() {
    LoggerService.log('OfferListComponent::ngAfterViewInit');
    this.paginator.page.pipe(tap(() => this.loadData())).subscribe();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  withdrawOffers(): void {
    const activeTags = this.activeTags.map(activeTag => activeTag.value);
    activeTags.push(this.selectedStatus.statusCode);
    let query = activeTags.length ? { 'tags.value': { $all: activeTags } } : {};
    query = Object.assign(query, { offerStatus: { $ne: 'withdrawn' } });
    const dialogRef = this.dialog.open(WithdrawOffersDialogComponent, { disableClose: true });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        return this.offersService.withdraw(query).subscribe(() => {
          this.loadData();
        });
      }
    });
  }

  filterByTags($event: Tag[]) {
    this.paginator.firstPage();
    this.activeTags = $event;
    this.loadData();
  }

  loadData(): void {
    let query = {};
    const activeTags = this.activeTags.map(activeTag => activeTag.value);
    if (activeTags.length) {
      query = Object.assign(query, { 'tags.value': { $all: activeTags } });
    }
    query = Object.assign(query, {
      offerStatus: this.checkIfSelectedStatus()
    });
    this.dataSource.loadData(query, this.paginator.pageIndex, this.paginator.pageSize, 'status');
  }

  addEditOwner(event: Event, offer): void {
    event.stopPropagation();
    this.offersService.update(offer._id, offer).subscribe(
      data => {
        if (data) {
          this.loadData();
          this.toastr.success(this.translate.instant('OFFERS_LIST.COMPONENT.EDIT_OWNER.SUCCESS'));
        }
      },
      err => {
        this.toastr.error(
          this.translate.instant('OFFERS_LIST.COMPONENT.EDIT_OWNER.ERROR'),
          this.translate.instant('OFFERS_LIST.COMPONENT.EDIT_OWNER.ERROR_TITLE')
        );
      }
    );
  }

  exportCSV(): void {
    let query = {};
    const activeTags = this.activeTags.map(activeTag => activeTag.value);
    if (activeTags.length) {
      query = Object.assign(query, { 'tags.value': { $all: activeTags } });
    }
    query = Object.assign(query, { offerStatus: this.selectedStatus.statusCode });
    this.isLoadingResults = true;
    this.offersService.downloadCSV(query).subscribe(
      data => {
        this.isLoadingResults = false;
        OfferListComponent.downloadFile(data);
      },
      error => console.log('Error downloading the file.'),
      () => console.log('File downloaded.')
    );
  }

  openMassStatusChangeModal() {
    this.dialog
      .open(MassStatusChangeDialogComponent, {
        disableClose: true,
        data: {
          activeTags: this.activeTags,
          closeOnNavigation: true,
          selectedStatus: this.selectedStatus
        }
      })
      .afterClosed()
      .subscribe(success => {
        if (success) {
          this.setStatusesCount();
          this.toastr.success(this.translate.instant('OFFERS_LIST.COMPONENT.MASS_STATUS_SUCCESS'));
        }
      });
  }

  openMassTagsChangeModal() {
    this.dialog
      .open(MassTagsChangeDialogComponent, {
        disableClose: true,
        data: {
          activeTags: this.activeTags,
          closeOnNavigation: true,
          selectedStatus: this.selectedStatus
        }
      })
      .afterClosed()
      .subscribe(
        success => {
          if (success) {
            this.setStatusesCount();
            this.TagsFilter.findTags({ offerStatus: this.selectedStatus.statusCode });
            return this.toastr.success(this.translate.instant('OFFERS_LIST.COMPONENT.MASS_TAGS.SUCCESS'));
          }
        },
        error => this.toastr.error(this.translate.instant('OFFERS_LIST.COMPONENT.MASS_TAGS.ERROR'))
      );
  }

  onSelectedStatusChange(event: StepperSelectionEvent) {
    LoggerService.log('OfferListComponent::onSelectesStatusChange');
    const selectedStatusIndex = event.selectedIndex;
    this.selectedStatus = this.statuses[selectedStatusIndex];

    this.loadData();
  }

  onMassEdit() {
    const statusName: string = this.selectedStatus.statusName.toLowerCase();
    this.router.navigate(['/mass-offers-edit'], { queryParams: { status: statusName } });
  }

  private setStatusesCount(): void {
    this.statusService.getStatuses().subscribe(
      statuses => {
        this.statuses = statuses.sort((a, b) =>
          a.orderNumber < b.orderNumber ? -1 : a.orderNumber > b.orderNumber ? 1 : 0
        );
        this.loadData();
        this.offersFilteredByStatusCode = this.statuses.map(status => {
          return {
            status: status,
            count: { matchingResults: 0 },
            isToggled: true
          };
        });

        this.offersFilteredByStatusCode.forEach(offerCount => {
          this.offersService.count({ offerStatus: offerCount.status.statusCode }).subscribe(count => {
            offerCount.count = count;
          });
        });
      },
      error => {
        LoggerService.log('Offers by status count; Data fetch error', error);
        this.toastr.error(this.translate.instant('OFFERS_LIST.COMPONENT.STATUSES_COUNT_ERROR'));
      }
    );
  }

  private checkIfSelectedStatus(): string {
    if (!this.selectedStatus) {
      this.selectedStatus =
        this.statuses.find(status => status.statusCode === this.offersService.currentStatus$.value) || this.statuses[0];
    }
    const index = this.statuses.findIndex(status => status.statusCode === this.selectedStatus.statusCode);
    this.stepperSelectedIndex = index === -1 ? 0 : index;
    this.offersService.currentStatus$.next(this.selectedStatus.statusCode);
    return this.selectedStatus.statusCode;
  }

  openMetadataDialog(event, metadata): void {
    event.stopPropagation();
    this.dialog.open(OfferInitialCsvDialogComponent, { minWidth: '500px', autoFocus: true, data: metadata });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/offers/offer-list/offer-list.component.ts